import React from 'react';
import { Icon } from './icons/Icon';
import { PermissionGate } from './PermissionGate';
import { useTranslation } from '../hooks/useTranslation';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../utils/language.utils';

export interface CrudActionButtonsPermissions {
    view?: string;
    edit?: string;
    copy?: string;
    delete?: string;
}

export interface CrudActionButtonsProps {
    language?: SupportedLanguage;
    permissions?: CrudActionButtonsPermissions;
    onView?: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
    onDelete?: () => void;
    disableDelete?: boolean;
    viewTitle?: string;
    editTitle?: string;
    copyTitle?: string;
    deleteTitle?: string;
    customActions?: React.ReactNode;
}

export const CrudActionButtons: React.FC<CrudActionButtonsProps> = ({
    language = DEFAULT_LANGUAGE,
    permissions,
    onView,
    onEdit,
    onCopy,
    onDelete,
    disableDelete = false,
    viewTitle,
    editTitle,
    copyTitle,
    deleteTitle,
    customActions
}) => {
    const trans = useTranslation(language);
    const t = {
        view: viewTitle || trans.common.view,
        edit: editTitle || trans.common.edit,
        copy: copyTitle || trans.common.copy,
        delete: deleteTitle || trans.common.delete
    };

    const wrap = (permission: string | undefined, node: React.ReactNode) => {
        if (!permission) return <>{node}</>;
        return <PermissionGate permission={permission}>{node}</PermissionGate>;
    };

    return (
        <div className="action-buttons">
            {onView && wrap(permissions?.view, (
                <button
                    type="button"
                    className="btn-view"
                    onClick={(e) => {
                        e.stopPropagation();
                        onView();
                    }}
                    title={t.view}
                >
                    <Icon name="view" size={18} />
                </button>
            ))}

            {onEdit && wrap(permissions?.edit, (
                <button
                    type="button"
                    className="btn-edit"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    title={t.edit}
                >
                    <Icon name="edit" size={18} />
                </button>
            ))}

            {onCopy && wrap(permissions?.copy, (
                <button
                    type="button"
                    className="btn-copy"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCopy();
                    }}
                    title={t.copy}
                >
                    <Icon name="copy" size={18} />
                </button>
            ))}

            {onDelete && wrap(permissions?.delete, (
                <button
                    type="button"
                    className="btn-delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title={t.delete}
                    disabled={disableDelete}
                >
                    <Icon name="delete" size={18} />
                </button>
            ))}

            {customActions && customActions}
        </div>
    );
};
